/**
 * PendingReferrerApprovals
 * Shows pending referrer registrations on the admin dashboard
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserCheck, UserX, Building, Mail, Phone, Clock } from 'lucide-react';
import API_BASE_URL from '../config/api';

function PendingReferrerApprovals() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchPending();
  }, []);

  const fetchPending = async () => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_BASE_URL}/referrer-auth/admin/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPending(response.data.referrers || []);
    } catch (err) {
      // Silently fail - user might not be admin or route might not exist
      console.log('Could not fetch pending referrers:', err.response?.status);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (id, action) => {
    setActionLoading(id);
    try {
      const token = sessionStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/referrer-auth/admin/approve/${id}`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPending(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      console.error('Error updating referrer:', err);
      alert('Failed to update referrer: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-NZ', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  if (loading) return null;
  if (pending.length === 0) return null;

  return (
    <div style={{
      backgroundColor: '#fff3cd',
      border: '1px solid #ffc107',
      borderRadius: 8,
      padding: 15,
      marginBottom: 20,
    }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#856404', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Clock size={16} />
        Pending Referrer Approvals ({pending.length})
      </h3>

      {pending.map(referrer => (
        <div key={referrer.id} style={{
          backgroundColor: 'white',
          borderRadius: 6,
          padding: 12,
          marginBottom: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 600, color: '#333', marginBottom: 3 }}>
              {referrer.name}
            </div>
            <div style={{ fontSize: 13, color: '#666', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Building size={12} /> {referrer.practiceName}
            </div>
            <div style={{ fontSize: 12, color: '#999', display: 'flex', gap: 12, marginTop: 3 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Mail size={11} /> {referrer.email}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Phone size={11} /> {referrer.phone}
              </span>
            </div>
            <div style={{ fontSize: 11, color: '#bbb', marginTop: 3 }}>
              Registered: {formatDate(referrer.createdAt)}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => handleAction(referrer.id, 'approve')}
              disabled={actionLoading === referrer.id}
              style={{
                padding: '8px 16px',
                backgroundColor: '#5cb85c',
                color: 'white',
                border: 'none',
                borderRadius: 5,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                opacity: actionLoading === referrer.id ? 0.6 : 1,
              }}
            >
              <UserCheck size={14} /> Approve
            </button>
            <button
              onClick={() => handleAction(referrer.id, 'reject')}
              disabled={actionLoading === referrer.id}
              style={{
                padding: '8px 16px',
                backgroundColor: '#d9534f',
                color: 'white',
                border: 'none',
                borderRadius: 5,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                opacity: actionLoading === referrer.id ? 0.6 : 1,
              }}
            >
              <UserX size={14} /> Reject
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default PendingReferrerApprovals;
